import { QueryStatsOutlined } from '@mui/icons-material';
import { Chip, Skeleton, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import RevenueChart from './revenueChart';
import RevenueSummaryCards from './revenueSummaryCards';
import Result from 'components/v2/common/Result';
import { SectionCard } from 'components/v2/common/sectionCard';

import {
    getMonthlyAssessmentRevenue,
    getMonthlyBillingRevenue,
    getRevenueCurrencies,
} from 'helpers/serviceProviderBilling';
import useTranslate from 'hooks/useTranslate';
import type { BillingRevenueCountryCode, RevenueChartGranularity, RevenueChartMode } from 'types';
import { useBillingRevenue } from 'views/v2/serviceProviderBilling/hooks/useBillingRevenue';

interface RevenuePanelProps {
    /** `null` shows the total across all service providers. */
    providerId: number | null;
    from: string | undefined;
    to: string | undefined;
    countryCodes: BillingRevenueCountryCode[] | undefined;
    granularity: RevenueChartGranularity;
}

const RevenuePanel = ({ providerId, from, to, countryCodes, granularity }: RevenuePanelProps) => {
    const { translate } = useTranslate();
    const { revenue, loading } = useBillingRevenue(providerId, from, to, countryCodes);
    const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
    const [chartMode, setChartMode] = useState<RevenueChartMode>(
        granularity === 'month' ? 'monthly' : 'cumulative'
    );
    useEffect(() => {
        setChartMode(granularity === 'month' ? 'monthly' : 'cumulative');
    }, [granularity]);
    const effectiveChartMode = granularity === 'month' ? chartMode : 'cumulative';
    const currencies = useMemo(() => (revenue ? getRevenueCurrencies(revenue) : []), [revenue]);
    const servicePoints = useMemo(
        () =>
            revenue
                ? granularity === 'month'
                    ? getMonthlyBillingRevenue(revenue.daily)
                    : revenue.daily
                : [],
        [granularity, revenue]
    );
    const assessmentPoints = useMemo(
        () =>
            revenue
                ? granularity === 'month'
                    ? getMonthlyAssessmentRevenue(revenue.assessmentRevenue.daily)
                    : revenue.assessmentRevenue.daily
                : [],
        [granularity, revenue]
    );
    const currency =
        selectedCurrency && currencies.includes(selectedCurrency)
            ? selectedCurrency
            : (currencies[0] ?? null);
    const hasRevenue = Boolean(
        revenue &&
        [...revenue.daily, ...revenue.assessmentRevenue.daily].some((point) =>
            Object.values(point.amounts).some((amount) => amount > 0)
        )
    );

    const chart = (() => {
        if (loading) {
            return <Skeleton variant="rounded" height={320} />;
        }
        if (!revenue) {
            return null;
        }
        if (!hasRevenue || !currency) {
            return (
                <Result
                    icon={<QueryStatsOutlined sx={{ fontSize: 32 }} />}
                    title={translate('serviceProviderBilling.revenue.emptyTitle')}
                    description={translate('serviceProviderBilling.revenue.emptyDescription')}
                    sx={{ border: 'none', background: 'transparent' }}
                />
            );
        }
        return (
            <RevenueChart
                servicePoints={servicePoints}
                assessmentPoints={assessmentPoints}
                currency={currency}
                granularity={granularity}
                mode={effectiveChartMode}
                onModeChange={setChartMode}
                rangeFrom={revenue.filters.from}
                rangeTo={revenue.filters.to}
            />
        );
    })();

    const currencyControl =
        currency && revenue ? (
            currencies.length === 1 ? (
                <Chip label={currency} size="small" variant="outlined" />
            ) : (
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={currency}
                    onChange={(_event, nextCurrency: string | null) => {
                        if (nextCurrency) {
                            setSelectedCurrency(nextCurrency);
                        }
                    }}
                    aria-label={translate('serviceProviderBilling.revenue.currency')}
                >
                    {currencies.map((item) => (
                        <ToggleButton key={item} value={item} aria-label={item}>
                            {item}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            )
        ) : null;

    return (
        <Stack spacing={2.5}>
            <RevenueSummaryCards loading={loading} revenue={revenue} currency={currency} />
            <SectionCard
                title={translate(
                    granularity === 'month' && effectiveChartMode === 'monthly'
                        ? 'serviceProviderBilling.revenue.monthlyChartTitle'
                        : 'serviceProviderBilling.revenue.chartTitle'
                )}
                actions={currencyControl}
            >
                {chart}
            </SectionCard>
        </Stack>
    );
};

export default RevenuePanel;
